import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';
import { DEFAULT_SEARCH_RADIUS_IN_METERS } from 'features/store/constants';
import { useAutoSelectStoreUtils } from 'features/store/hooks/use-auto-select-store-utils';
import { useNearbyRestaurants } from 'features/store/pages/pickup-store-selector/hooks/helpers/use-restaurants';

import { ILocation } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { OperationalStatus } from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { logger } from 'utils/logger';

import { NearbyStoreData } from '../types';
import { resolveCloserRestaurantCandidate, roundCoordinateForSearch } from '../utils';

/**
 * Fetches nearby stores and resolves the closest eligible restaurant for the
 * auto-select closest store prompt. Callers should pass the freshest known
 * user coordinates so recommendations are not based on a stale location.
 */
export const useNearbyStoreData = ({
  userCoordinates,
}: {
  userCoordinates: ILocation;
}): {
  nearbyStoreData: NearbyStoreData | null;
  setNearbyStoreData: Dispatch<SetStateAction<NearbyStoreData | null>>;
  fetchNearbyRestaurant: (coordinates?: ILocation) => Promise<void>;
} => {
  const [nearbyStoreData, setNearbyStoreData] = useState<NearbyStoreData | null>(null);
  const fetchRequestIdRef = useRef(0);
  const { fetch: fetchNearbyStores } = useNearbyRestaurants();
  const { isAutoSelectableStore } = useAutoSelectStoreUtils();
  const { store: activeStore } = useStoreContext();

  const defaultDiagonal =
    useFlag(LaunchDarklyFlag.RADIUS_FOR_STORE_LOCATOR) || DEFAULT_SEARCH_RADIUS_IN_METERS;

  const fetchNearbyRestaurant = useCallback(
    async (coordinates?: ILocation) => {
      const location = coordinates ?? userCoordinates;
      const requestId = ++fetchRequestIdRef.current;

      const nearbyStores = await fetchNearbyStores(
        {
          first: 10,
          status: OperationalStatus.OPEN,
          radiusStrictMode: true,
          coordinates: {
            searchRadius: defaultDiagonal,
            userLat: roundCoordinateForSearch(location.lat),
            userLng: roundCoordinateForSearch(location.lng),
          },
        },
        { allowFromCache: false }
      );

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      const restaurants = (nearbyStores ?? []) as IRestaurant[];
      const candidate = resolveCloserRestaurantCandidate({
        userLocation: location,
        nearbyRestaurants: restaurants,
        activeStore,
        isAvailable: isAutoSelectableStore,
      });

      if (!candidate) {
        if (restaurants.length) {
          logger.warn({
            message: 'No closer restaurant candidate for current location',
            activeStoreId: activeStore?._id,
            userCoordinates: location,
          });
        }
        setNearbyStoreData(null);
        return;
      }

      setNearbyStoreData({
        restaurant: candidate,
      });
    },
    [userCoordinates, activeStore, defaultDiagonal, fetchNearbyStores, isAutoSelectableStore]
  );

  return { nearbyStoreData, setNearbyStoreData, fetchNearbyRestaurant };
};
